/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Loading from '../../components/Loading';
import axios from '../../services/axios';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../styles/layout/Section';
import { FormGrid } from '../../styles/layout/FormGrid';
import { Field } from '../../styles/forms/Field';
import { ActionBar, ActionBarRight } from '../../styles/layout/ActionBar';
import { EmptyState } from '../../styles/feedback/EmptyState';
import ProductNameSearch from '../../components/ProductNameSearch';

// =====================
// Estilos Locais
// =====================

const ResultCard = styled.div`
   background: ${({ theme }) => theme.colors.surface};
   border: 1px solid ${({ theme }) => theme.colors.border};
   border-radius: 12px;
   padding: 24px;
   display: grid;
   gap: 20px;
`;

const CardHeader = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: flex-start;
   padding-bottom: 16px;
   border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ProductInfo = styled.div`
   h3 {
      margin: 0 0 4px 0;
      font-size: 18px;
      color: ${({ theme }) => theme.colors.textPrimary};
   }

   p {
      margin: 0;
      font-size: 14px;
      color: ${({ theme }) => theme.colors.textMuted};
   }
`;

const StockBadge = styled.div`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   padding: 12px 20px;
   border-radius: 12px;
   font-size: 24px;
   font-weight: 700;
   background: ${({ $status, theme }) => {
      if ($status === 'LOW') return 'rgba(255, 152, 0, 0.12)';
      if ($status === 'OUT') return 'rgba(244, 67, 54, 0.12)';
      return 'rgba(76, 175, 80, 0.12)';
   }};
   color: ${({ $status }) => {
      if ($status === 'LOW') return '#e65100';
      if ($status === 'OUT') return '#c62828';
      return '#2e7d32';
   }};
   border: 2px solid
      ${({ $status }) => {
         if ($status === 'LOW') return 'rgba(255, 152, 0, 0.4)';
         if ($status === 'OUT') return 'rgba(244, 67, 54, 0.4)';
         return 'rgba(76, 175, 80, 0.4)';
      }};
`;

const InfoGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
   gap: 16px;
`;

const InfoItem = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;

   label {
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      color: ${({ theme }) => theme.colors.textMuted};
      letter-spacing: 0.5px;
   }

   span {
      font-size: 16px;
      color: ${({ theme }) => theme.colors.textPrimary};
      font-weight: 500;
   }
`;

export default function StockHistoryByDate() {
   const [pdvs, setPdvs] = useState([]);
   const [isLoading, setIsLoading] = useState(false);

   // Data padrão: hoje
   const today = new Date()
      .toLocaleDateString('pt-BR', {
         year: 'numeric',
         month: '2-digit',
         day: '2-digit',
      })
      .split('/')
      .reverse()
      .join('-');

   // Filtros
   const [date, setDate] = useState(today);
   const [pdvId, setPdvId] = useState('');
   const [productQuery, setProductQuery] = useState('');
   const [selectedProduct, setSelectedProduct] = useState(null);

   // Resultado
   const [snapshot, setSnapshot] = useState(null);

   useEffect(() => {
      async function loadPdvs() {
         try {
            setIsLoading(true);
            const response = await axios.get('/pdvs');
            const raw = response?.data ?? [];
            const list = Array.isArray(raw) ? raw : raw?.pdvs ?? [];
            setPdvs(Array.isArray(list) ? list : []);
         } catch (err) {
            const msg =
               err?.response?.data?.errors?.[0] ?? 'Erro ao carregar PDVs.';
            toast.error(msg);
         } finally {
            setIsLoading(false);
         }
      }

      loadPdvs();
   }, []);

   const handleClearFilters = () => {
      const ok = window.confirm('Deseja limpar todos os filtros?');
      if (!ok) return;

      setDate(today);
      setPdvId('');
      setProductQuery('');
      setSelectedProduct(null);
      setSnapshot(null);
   };

   const handleSearch = async (e) => {
      e.preventDefault();

      // Validações
      if (!date) {
         toast.error('Informe a data');
         return;
      }

      if (!pdvId) {
         toast.error('Selecione um PDV');
         return;
      }

      // Produto (SKU ou autocomplete)
      const pq = (productQuery || '').trim();
      const isDigits = /^\d+$/.test(pq);

      let prodId = null;

      if (selectedProduct?.id) {
         prodId = selectedProduct.id;
      } else if (isDigits && pq.length === 7) {
         // SKU completo - buscar produto
         try {
            const prodRes = await axios.get(`/products/sku/${pq}`);
            prodId = prodRes?.data?.id;
         } catch {
            toast.error('Produto não encontrado com este SKU');
            return;
         }
      } else {
         toast.error('Selecione um produto ou digite o SKU completo (7 dígitos)');
         return;
      }

      setIsLoading(true);

      try {
         const response = await axios.get(
            `/stock/history/${prodId}/${pdvId}`,
            {
               params: { date },
            }
         );

         setSnapshot(response.data);
      } catch (error) {
         const msg =
            error?.response?.data?.errors?.[0] ??
            'Erro ao buscar histórico de estoque.';
         toast.error(msg);
         setSnapshot(null);
      } finally {
         setIsLoading(false);
      }
   };

   const getStockStatus = () => {
      if (!snapshot) return 'OK';
      if (snapshot.stock_qtd === 0) return 'OUT';
      if (snapshot.stock_qtd < snapshot.min_stock_qtd) return 'LOW';
      return 'OK';
   };

   const getStockStatusLabel = (status) => {
      if (status === 'LOW') return 'Abaixo do Mínimo';
      if (status === 'OUT') return 'Sem Estoque';
      return 'Estoque OK';
   };

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Consulta de Estoque por Data</PageTitle>
               <PageSubtitle>
                  Consulte o estoque de um produto em uma data específica
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>

            <SectionBody>
               <form onSubmit={handleSearch}>
                  <FormGrid>
                     <Field $col={3} $colMobile={12}>
                        <label>Data *</label>
                        <input
                           type="date"
                           value={date}
                           onChange={(e) => setDate(e.target.value)}
                           max={today}
                           required
                        />
                     </Field>

                     <Field $col={3} $colMobile={12}>
                        <label>PDV *</label>
                        <select
                           value={pdvId}
                           onChange={(e) => setPdvId(e.target.value)}
                           required
                        >
                           <option value="">Selecione...</option>
                           {pdvs.map((pdv) => (
                              <option key={String(pdv.id)} value={pdv.id}>
                                 {pdv.pdv_name ?? pdv.name ?? `${pdv.id}`}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={6} $colMobile={12}>
                        <label>Produto (SKU ou nome) *</label>
                        <ProductNameSearch
                           value={productQuery}
                           onChange={setProductQuery}
                           onSelect={setSelectedProduct}
                           nameLike={false}
                           displayMode="sku-name"
                           placeholder="Digite o SKU (7 dígitos) ou busque pelo nome..."
                           showHint
                        />
                     </Field>

                     <Field $col={12} $colMobile={12} $alignEnd>
                        <label>&nbsp;</label>
                        <ActionBar>
                           <ActionBarRight>
                              <button
                                 type="button"
                                 onClick={handleClearFilters}
                                 disabled={isLoading}
                              >
                                 Limpar
                              </button>
                              <button type="submit" disabled={isLoading}>
                                 Consultar
                              </button>
                           </ActionBarRight>
                        </ActionBar>
                     </Field>
                  </FormGrid>
               </form>
            </SectionBody>
         </Section>

         {snapshot && (
            <Section>
               <SectionHeader>
                  <SectionTitle>Resultado</SectionTitle>
               </SectionHeader>

               <SectionBody>
                  <ResultCard>
                     <CardHeader>
                        <ProductInfo>
                           <h3>{snapshot.prod_name}</h3>
                           <p>
                              SKU: {snapshot.prod_sku} • PDV: {snapshot.pdv_name}
                           </p>
                        </ProductInfo>

                        <StockBadge $status={getStockStatus()}>
                           {snapshot.stock_qtd} un.
                        </StockBadge>
                     </CardHeader>

                     <InfoGrid>
                        <InfoItem>
                           <label>Data do Snapshot</label>
                           <span>
                              {new Date(
                                 snapshot.snapshot_date + 'T00:00:00'
                              ).toLocaleDateString('pt-BR')}
                           </span>
                        </InfoItem>

                        <InfoItem>
                           <label>Estoque Mínimo</label>
                           <span>{snapshot.min_stock_qtd} un.</span>
                        </InfoItem>

                        <InfoItem>
                           <label>Status</label>
                           <span>{getStockStatusLabel(getStockStatus())}</span>
                        </InfoItem>

                        <InfoItem>
                           <label>Gerado em</label>
                           <span>
                              {new Date(
                                 snapshot.snapshot_created_at
                              ).toLocaleString('pt-BR')}
                           </span>
                        </InfoItem>
                     </InfoGrid>
                  </ResultCard>
               </SectionBody>
            </Section>
         )}

         {!snapshot && !isLoading && (
            <EmptyState
               title="Nenhum resultado"
               description="Preencha os filtros e clique em Consultar para visualizar o estoque."
            />
         )}
      </Container>
   );
}
